import { isTextKey } from '../i18n/i18n';
import type { TextKey } from '../i18n/i18n';
import { brainSettings } from '../rules/brain';
import { punchStats } from '../rules/types';
import type { PunchStats } from '../rules/types';
import type { Cards } from './cards';
import difficultyJson from './difficulty.json';

/**
 * A level of the game: how the opponent fights, as times his cards, and what a bout pays. The cards are the pro level,
 * so Sashko's tuning moves every level with them.
 */
export interface Level {
  readonly id: string;
  readonly nameKey: TextKey;
  /** A win or a draw pays the shop's coins times this. */
  readonly coinsTimes: number;
  /** The coins a loss takes. */
  readonly coinsLost: number;
  readonly health: number;
  readonly damage: number;
  /** His windups: the time the player has to see a punch coming. */
  readonly windup: number;
  /** His pauses between attacks. */
  readonly pause: number;
  /** How often he blocks and counters; never past every time. */
  readonly guard: number;
  /** How often an attack is a combo; never past every time. */
  readonly combo: number;
}

/** The levels from the easiest, and the one a new player starts on. */
export interface DifficultyCard {
  readonly levels: readonly Level[];
  readonly start: Level;
}

type Times = 'coinsTimes' | 'health' | 'damage' | 'windup' | 'pause' | 'guard' | 'combo';
const times: readonly Times[] = ['coinsTimes', 'health', 'damage', 'windup', 'pause', 'guard', 'combo'];

export function loadDifficulty(): DifficultyCard {
  const levels = difficultyJson.levels.map((raw): Level => {
    for (const name of times) {
      if (!Number.isFinite(raw[name]) || raw[name] <= 0) throw new Error(`Level ${raw.id}: ${name} must be more than 0, not ${raw[name]}`);
    }
    if (!Number.isInteger(raw.coinsLost) || raw.coinsLost < 0) throw new Error(`Level ${raw.id}: coinsLost must be a whole number of at least 0`);
    if (!isTextKey(raw.nameKey)) throw new Error(`Level ${raw.id}: no text for "${raw.nameKey}"`);
    return { ...raw, nameKey: raw.nameKey };
  });
  if (new Set(levels.map((l) => l.id)).size !== levels.length) throw new Error('Two levels have the same id');
  const start = levels.find((l) => l.id === difficultyJson.start);
  if (!start) throw new Error(`No level "${difficultyJson.start}" to start on`);
  return { levels, start };
}

/** The cards with the opponent made as hard as the level. */
export function withDifficulty(cards: Cards, level: Level): Cards {
  const changed = new Map<PunchStats, PunchStats>();
  const opponentPunches = Object.fromEntries(
    Object.entries(cards.opponentPunches).map(([id, punch]) => {
      const made = punchStats({ ...punch, damage: Math.round(punch.damage * level.damage), windupSeconds: punch.windupSeconds * level.windup });
      changed.set(punch, made);
      return [id, made];
    }),
  );
  const brain = cards.opponent.brain;
  return {
    ...cards,
    opponentPunches,
    opponent: {
      ...cards.opponent,
      health: Math.round(cards.opponent.health * level.health),
      brain: brainSettings({
        ...brain,
        pauseMinSeconds: brain.pauseMinSeconds * level.pause,
        pauseMaxSeconds: brain.pauseMaxSeconds * level.pause,
        blockChance: Math.min(1, brain.blockChance * level.guard),
        counterChance: Math.min(1, brain.counterChance * level.guard),
        comboChance: Math.min(1, brain.comboChance * level.combo),
        favourites: brain.favourites.map((f) => ({ punch: changed.get(f.punch)!, weight: f.weight })),
      }),
    },
  };
}
